import { Button, Flex, Space, Tooltip } from 'antd'
import { ArrowLeft, Ban, Pencil, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

type Props = {
  /** Where the back link routes to (usually the list page). */
  backTo: string
  /** Back link label, e.g. `Back to CRM credentials`. */
  backLabel: ReactNode
  /** Revoked or expired — disables Edit (only active credentials are editable). */
  locked: boolean
  /**
   * Deliberately revoked — disables Rotate and Revoke so a stale click can't
   * mutate an already-dead token. Expired keeps both.
   */
  revoked: boolean
  /**
   * When present, renders an Edit icon-only button first. Detail pages that
   * don't support edit (e.g. read-only screens) can omit this.
   */
  onEdit?: () => void
  onRotate: () => void
  onRevoke: () => void
  /** Localised label for the Rotate button (feature-scoped). */
  rotateLabel: string
  /** Localised label for the Revoke button (feature-scoped). */
  revokeLabel: string
}

/**
 * Shared toolbar for credential detail pages — Back link on the left, action
 * cluster (Edit / Rotate / Revoke) on the right. Both public-access and
 * app-credential detail pages render the same shape, so a shared component
 * keeps the two in sync on button order, icons, and disabled semantics.
 */
export const CredentialDetailToolbar = ({
  backTo,
  backLabel,
  locked,
  revoked,
  onEdit,
  onRotate,
  onRevoke,
  rotateLabel,
  revokeLabel
}: Props) => {
  const { t } = useTranslation()

  return (
    <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
      <Link to={backTo}>
        <Button size="small" icon={<ArrowLeft size={14} />} type="text">
          {backLabel}
        </Button>
      </Link>
      <Space size={8}>
        {onEdit ? (
          <Tooltip title={t('edit', 'Edit')}>
            <Button
              icon={<Pencil size={14} />}
              disabled={locked}
              onClick={onEdit}
            >
              {t('edit', 'Edit')}
            </Button>
          </Tooltip>
        ) : null}
        <Tooltip title={rotateLabel}>
          <Button
            icon={<RefreshCw size={14} />}
            disabled={revoked}
            onClick={onRotate}
          >
            {rotateLabel}
          </Button>
        </Tooltip>
        <Tooltip title={revokeLabel}>
          <Button
            icon={<Ban size={14} />}
            danger
            disabled={revoked}
            onClick={onRevoke}
          >
            {revokeLabel}
          </Button>
        </Tooltip>
      </Space>
    </Flex>
  )
}
